/**
 * 用户认证相关API
 * 包括登录、注册、验证码等功能
 */

import Logger from "../utils/logger";
import axiosInstance from "../services/axiosConfig";
import { API_PATHS, STORAGE_KEYS } from "./config";
import { getCurrentUser, saveCurrentUser } from "./utils";

/**
 * 发送验证码
 * @param {string} email 用户邮箱
 * @returns {Promise<Object>} 包含success和message的响应对象
 */
export const sendVerificationCode = async (email) => {
  try {
    if (!email || typeof email !== "string") {
      return { success: false, message: "邮箱地址不能为空" };
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: "请输入有效的邮箱地址" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.SEND_CODE}`,
      { email }
    );

    Logger.info("验证码发送结果", { email, success: response.success });

    return response;
  } catch (error) {
    Logger.error("发送验证码失败", error);
    return error.success === false ? error : { success: false, message: "发送验证码失败，请稍后再试" };
  }
};

/**
 * 验证邮箱验证码
 * @param {string} email 用户邮箱
 * @param {string} code 验证码
 * @returns {Promise<Object>} 包含success和message的响应对象
 */
export const verifyCode = async (email, code) => {
  try {
    // 基本检查
    if (!email || !code) {
      return { success: false, message: "邮箱和验证码不能为空" };
    }

    // 此函数不直接调用后端API，而是在登录/注册函数中使用验证码
    return { success: true, message: "验证成功" };
  } catch (error) {
    Logger.error("验证码验证失败", error);
    return error.success === false ? error : { success: false, message: "验证失败，请稍后再试" };
  }
};

/**
 * 使用邮箱验证码注册新用户
 * @param {string} email 用户邮箱
 * @param {string} verificationCode 验证码
 * @returns {Promise<Object>} 包含success、message和user的响应对象
 */
export const registerUser = async (email, verificationCode) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!verificationCode) {
      return { success: false, message: "验证码不能为空" };
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: "请输入有效的邮箱地址" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.REGISTER}`,
      {
        email,
        code: verificationCode,
      }
    );

    if (response.success) {
      // 注册成功，保存用户信息到本地存储
      saveCurrentUser(response.data);
      Logger.info("用户注册成功", { email });
    } else {
      Logger.warn("用户注册失败", { email, message: response.message });
    }
    return response;
  } catch (error) {
    Logger.error("用户注册失败", error);
    return error.success === false ? error : { success: false, message: "注册失败，请稍后再试" };
  }
};

/**
 * 使用邮箱和密码注册新用户
 * @param {string} email 用户邮箱
 * @param {string} password 密码
 * @param {string} confirmPassword 确认密码
 * @param {string} verificationCode 验证码
 * @returns {Promise<Object>} 包含success、message和user的响应对象
 */
export const registerWithPassword = async (email, password, confirmPassword, verificationCode, referralCode = null) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!password) {
      return { success: false, message: "密码不能为空" };
    }
    
    if (!confirmPassword) {
      return { success: false, message: "确认密码不能为空" };
    }
    
    if (password !== confirmPassword) {
      return { success: false, message: "两次输入的密码不一致" };
    }

    if (!verificationCode) {
      return { success: false, message: "验证码不能为空" };
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: "请输入有效的邮箱地址" };
    }

    // 验证密码强度
    if (password.length < 6) {
      return { success: false, message: "密码长度至少为6位" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.REGISTER_PASSWORD}`,
      {
        email,
        password,
        confirmPassword,
        code: verificationCode,
        referralCode: referralCode || undefined,
      }
    );

    if (response.success) {
      // 注册成功，保存用户信息到本地存储
      saveCurrentUser(response.data);
      Logger.info("用户注册成功", { email });
    } else {
      Logger.warn("用户注册失败", { email, message: response.message });
    }
    return response;
  } catch (error) {
    Logger.error("用户注册失败", error);
    return error.success === false ? error : { success: false, message: "注册失败，请稍后再试" };
  }
};

/**
 * 用户登录
 * @param {string} email 用户邮箱
 * @param {string} verificationCode 验证码
 * @returns {Promise<Object>} 包含success、message和user的响应对象
 */
export const loginUser = async (email, verificationCode) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!verificationCode) {
      return { success: false, message: "验证码不能为空" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.LOGIN}`,
      {
        email,
        code: verificationCode,
      }
    );

    if (response.success) {
      // 登录成功，保存用户信息到本地存储
      saveCurrentUser(response.data);
      Logger.info("用户登录成功", { email });
    } else {
      Logger.warn("用户登录失败", { email, message: response.message });
    }

    return response;
  } catch (error) {
    Logger.error("用户登录失败", error);
    return error.success === false ? error : { success: false, message: "登录失败，请稍后再试" };
  }
};

/**
 * 使用邮箱和密码登录
 * @param {string} email 用户邮箱
 * @param {string} password 密码
 * @returns {Promise<Object>} 包含success、message和user的响应对象
 */
export const loginWithPassword = async (email, password) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!password) {
      return { success: false, message: "密码不能为空" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.LOGIN_PASSWORD}`,
      {
        email,
        password,
      }
    );

    if (response.success) {
      // 登录成功，保存用户信息到本地存储
      saveCurrentUser(response.data);
      Logger.info("用户登录成功", { email });
    } else {
      Logger.warn("用户登录失败", { email, message: response.message });
    }

    return response;
  } catch (error) {
    Logger.error("用户登录失败", error);
    return error.success === false ? error : { success: false, message: "登录失败，请稍后再试" };
  }
};

/**
 * 处理登出
 * @returns {Object} 包含success和message的响应对象
 */
export const logoutUser = () => {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
  return { success: true, message: "登出成功" };
};

/**
 * 修改密码
 * @param {string} email 用户邮箱
 * @param {string} oldPassword 旧密码
 * @param {string} newPassword 新密码
 * @param {string} confirmPassword 确认新密码
 * @returns {Promise<Object>} 包含success和message的响应对象
 */
export const changePassword = async (email, oldPassword, newPassword, confirmPassword) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!oldPassword) {
      return { success: false, message: "旧密码不能为空" };
    }

    if (!newPassword) {
      return { success: false, message: "新密码不能为空" };
    }

    if (!confirmPassword) {
      return { success: false, message: "确认密码不能为空" };
    }

    if (newPassword !== confirmPassword) {
      return { success: false, message: "两次输入的密码不一致" };
    }

    // 验证密码强度
    if (newPassword.length < 6) {
      return { success: false, message: "密码长度至少为6位" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.CHANGE_PASSWORD}`,
      {
        email,
        oldPassword,
        newPassword,
        confirmPassword,
      }
    );

    if (response.success) {
      Logger.info("密码修改成功", { email });
    } else {
      Logger.warn("密码修改失败", { email, message: response.message });
    }
    return response;
  } catch (error) {
    Logger.error("修改密码失败", error);
    return error.success === false ? error : { success: false, message: "修改密码失败，请稍后再试" };
  }
};

/**
 * 重置密码（通过验证码）
 * @param {string} email 用户邮箱
 * @param {string} code 验证码
 * @param {string} newPassword 新密码
 * @param {string} confirmPassword 确认新密码
 * @returns {Promise<Object>} 包含success和message的响应对象
 */
export const resetPassword = async (email, code, newPassword, confirmPassword) => {
  try {
    if (!email) {
      return { success: false, message: "邮箱不能为空" };
    }

    if (!code) {
      return { success: false, message: "验证码不能为空" };
    }

    if (!newPassword) {
      return { success: false, message: "新密码不能为空" };
    }

    if (!confirmPassword) {
      return { success: false, message: "确认密码不能为空" };
    }

    if (newPassword !== confirmPassword) {
      return { success: false, message: "两次输入的密码不一致" };
    }

    // 验证密码强度
    if (newPassword.length < 6) {
      return { success: false, message: "密码长度至少为6位" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.RESET_PASSWORD}`,
      {
        email,
        code,
        newPassword,
        confirmPassword,
      }
    );

    if (response.success) {
      Logger.info("密码重置成功", { email });
    } else {
      Logger.warn("密码重置失败", { email, message: response.message });
    }
    return response;
  } catch (error) {
    Logger.error("重置密码失败", error);
    return error.success === false ? error : { success: false, message: "重置密码失败，请稍后再试" };
  }
};

/**
 * 获取邮箱域名白名单
 * @returns {Promise<Object>} 包含success和data.domains的响应对象
 */
export const getEmailWhitelist = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.AUTH.EMAIL_WHITELIST}`
    );
    return response;
  } catch (error) {
    Logger.error("获取邮箱白名单失败", error);
    return { success: false, message: "获取邮箱白名单失败", data: { domains: [] } };
  }
};

/**
 * 前端校验邮箱域名是否在白名单中
 * @param {string} email 用户邮箱
 * @param {Array<string>} whitelist 白名单域名列表
 * @returns {Object} { valid: boolean, message: string }
 */
export const validateEmailDomain = (email, whitelist) => {
  if (!email || !email.includes("@")) {
    return { valid: false, message: "请输入有效的邮箱地址" };
  }

  // 如果白名单为空，不限制
  if (!whitelist || whitelist.length === 0) {
    return { valid: true, message: "" };
  }

  const domain = email.split("@").pop().toLowerCase().trim();
  if (whitelist.map(d => d.toLowerCase()).includes(domain)) {
    return { valid: true, message: "" };
  }

  const domainList = whitelist.join("、");
  return { valid: false, message: `仅支持以下邮箱域名：${domainList}` };
};

export default {
  sendVerificationCode,
  verifyCode,
  registerUser,
  registerWithPassword,
  loginUser,
  loginWithPassword,
  logoutUser,
  changePassword,
  resetPassword,
  getEmailWhitelist,
  validateEmailDomain,
};
